import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { ListingCategory, ListingType, TransactionProcessAlias, UnitType } from '../../util/enums';
import {
  entityRefs,
  isArrayLength,
  showToaster,
  getOwnMoodboardListings,
} from '../../util/genericHelpers';
import moment from 'moment';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedEntities, updatedEntities } from '../../util/data';

// ================ Action types ================ //

export const CREATE_PROFILE_REQUEST = 'app/DreamHomePage/CREATE_PROFILE_REQUEST';
export const CREATE_PROFILE_SUCCESS = 'app/DreamHomePage/CREATE_PROFILE_SUCCESS';
export const CREATE_PROFILE_ERROR = 'app/DreamHomePage/CREATE_PROFILE_ERROR';

export const FETCH_ALL_LISTINGS_REQUEST = 'app/DreamHomePage/FETCH_ALL_LISTINGS_REQUEST';
export const FETCH_ALL_LISTINGS_SUCCESS = 'app/DreamHomePage/FETCH_ALL_LISTINGS_SUCCESS';
export const FETCH_ALL_LISTINGS_ERROR = 'app/DreamHomePage/FETCH_ALL_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  createProfileInProgress: false,
  createProfileError: null,
  fetchAllListingsInProgress: false,
  fetchAllListingsError: null,
  allListings: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CREATE_PROFILE_REQUEST:
      return { ...state, createProfileInProgress: true, createProfileError: null };
    case CREATE_PROFILE_SUCCESS:
      return { ...state, createProfileInProgress: false };
    case CREATE_PROFILE_ERROR:
      return { ...state, createProfileInProgress: false, createProfileError: payload };
    case FETCH_ALL_LISTINGS_REQUEST:
      return { ...state, fetchAllListingsInProgress: true, fetchAllListingsError: null };
    case FETCH_ALL_LISTINGS_SUCCESS:
      return { ...state, fetchAllListingsInProgress: false, allListings: payload };
    case FETCH_ALL_LISTINGS_ERROR:
      return { ...state, fetchAllListingsInProgress: false, fetchAllListingsError: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //
export const createProfileInProgressSelector = state => state.DreamHomePage.createProfileInProgress;
export const fetchAllListingsInProgressSelector = state =>
  state.DreamHomePage.fetchAllListingsInProgress;
export const allListingsSelector = state => state.DreamHomePage.allListings;

// ================ Action creators ================ //

export const createProfileRequest = () => ({ type: CREATE_PROFILE_REQUEST });

export const createProfileSuccess = () => ({ type: CREATE_PROFILE_SUCCESS });

export const createProfileError = e => ({
  type: CREATE_PROFILE_ERROR,
  error: true,
  payload: e,
});

export const fetchAllListingsRequest = () => ({ type: FETCH_ALL_LISTINGS_REQUEST });
export const fetchAllListingsSuccess = listings => ({
  type: FETCH_ALL_LISTINGS_SUCCESS,
  payload: listings,
});
export const fetchAllListingsError = e => ({
  type: FETCH_ALL_LISTINGS_ERROR,
  error: true,
  payload: e,
});
// ================ Thunks ================ //

/**
 * Creates a profile listing for the current user, or updates an existing one.
 *
 * The payload is constructed from the provided parameters, and then passed to
 * the SDK's create or update listing endpoint.
 *
 * @param {Object} params Parameters to construct the listing payload.
 * @param {Object} [params.plotAddress] Plot address data.
 * @param {String} [params.plotAddress.address] Plot address.
 * @param {Object} [params.plotAddress.selectedPlace] Plot address location data.
 * @param {Object} [params.plotAddress.selectedPlace.origin] Plot address location origin.
 * @param {String} [params.plotAddress.selectedPlace.address] Plot address.
 * @param {String} [params.listingId] ID of an existing listing to update.
 * @param {Boolean} [params.hasNoListing] If true, creates a new listing.
 * @param {String} [params.userFullName] User full name.
 * @param {Object} [params.moveInDate] Move in date data.
 * @param {String} [params.moveInDate.date] Move in date.
 * @param {Object} [params.onsiteStartDate] On-site start date data.
 * @param {String} [params.onsiteStartDate.date] On-site start date.
 * @param {Boolean} [params.isFinalStepCompleted] If true, profile creation is complete.
 * @param {Object} [params.publicData] Additional public data to include in the listing.
 * @returns {Promise<Object>} Listing data.
 */
export const createProfile = params => async (dispatch, getState, sdk) => {
  dispatch(createProfileRequest());

  try {
    // Extract parameters with safe defaults
    const {
      moveInDate = {},
      onsiteStartDate = {},
      plotAddress = {},
      listingId,
      isFinalStepCompleted,
      hasNoListing = false,
      userFullName = '',
      ...rest
    } = params;

    // Throw if no listing ID and not creating a new listing
    if (!listingId && !hasNoListing) {
      throw new Error('Listing ID is required for profile creation');
    }

    // Extract plot address data
    const { address = '', origin = null } = plotAddress.selectedPlace || {};

    // Construct public data with only defined and valid fields
    const publicData = {
      ...rest,
      ...(address && { plotAddress: address }),
      ...(moveInDate.date && { moveInDate: moment(moveInDate.date).unix() }),
      ...(onsiteStartDate.date && { onsiteStartDate: moment(onsiteStartDate.date).unix() }),
      transactionProcessAlias: TransactionProcessAlias.DEFAULT_INQUIRY,
      unitType: UnitType.INQUIRY,
      isFinalStepCompleted,
      listingType: ListingType.FREE_FLOW,
      listingCategory: ListingCategory.DREAM_HOME,
    };

    // Construct base payload with public data and optional geolocation
    const basePayload = {
      ...(origin && { geolocation: origin }),
      publicData,
    };

    // Construct listing payload depending on whether we are creating a new listing or updating an existing one
    const listingPayload = hasNoListing
      ? { ...basePayload, title: userFullName.trim() }
      : { ...basePayload, id: listingId };

    // Call the SDK endpoint to create or update the listing
    const response = hasNoListing
      ? await sdk.ownListings.create(listingPayload, { expand: true })
      : await sdk.ownListings.update(listingPayload, { expand: true });

    // Extract the listing data from the response
    const listing = response?.data?.data;

    // Throw if no listing data in response
    if (!listing?.id) {
      throw new Error('Invalid response data: Listing not found');
    }

    // Dispatch success action
    dispatch(createProfileSuccess());

    // Return the listing data
    return listing;
  } catch (error) {
    // Show error message
    const errorMessage = error?.message || 'Failed to create profile';
    showToaster('error', errorMessage);

    // Dispatch error action
    dispatch(createProfileError(storableError(error)));

    // Rethrow error
    throw error;
  }
};

export const fetchAllListings = (config, user) => async (dispatch, getState, sdk) => {
  dispatch(fetchAllListingsRequest());

  const listingsIfAny = getOwnMoodboardListings(user);
  const RESULT_PAGE_SIZE = 50;
  const delayBetweenBatches = 500;

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config.layout.listingImage || {};

  const aspectRatio = aspectHeight / aspectWidth;

  const queryParamsBase = {
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'images'],
    'fields.image': [
      'variants.default',
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    expand: true,
  };

  // ✅ Early return if no listings to fetch
  if (!isArrayLength(listingsIfAny)) {
    dispatch(fetchAllListingsSuccess([]));
    return [];
  }

  // Append IDs to query if listings exist
  Object.assign(queryParamsBase, {
    ids: listingsIfAny,
  });

  try {
    let allListings = [];
    let page = 1;

    const firstResponse = await sdk.listings.query({ ...queryParamsBase, page });
    const firstData = firstResponse?.data?.data || [];
    const totalPages = firstResponse?.data?.meta?.totalPages || 1;

    if (!isArrayLength(firstData)) {
      showToaster('error', 'No listings found');
      dispatch(fetchAllListingsError(new Error('No listings found')));
      return [];
    }

    const entities = updatedEntities({}, firstResponse.data);
    const listingRefs = entityRefs(firstResponse.data.data);
    const denormalisedListings = denormalisedEntities(entities, listingRefs, false);

    allListings.push(...denormalisedListings);

    for (page = 2; page <= totalPages; page++) {
      const res = await sdk.listings.query({ ...queryParamsBase, page });
      const batchListings = res?.data?.data || [];

      const listingRefs = entityRefs(batchListings);
      const denormalisedListings = denormalisedEntities(entities, listingRefs, false);

      allListings.push(...denormalisedListings);
      await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
    }

    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };

    dispatch(addMarketplaceEntities({ data: { data: allListings } }, sanitizeConfig));
    dispatch(fetchAllListingsSuccess(allListings));

    return allListings;
  } catch (error) {
    const errorMessage = error?.message || 'Failed to fetch listings';
    showToaster('error', errorMessage);
    dispatch(fetchAllListingsError(storableError(error)));
    throw error;
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const [user] = await Promise.all([dispatch(fetchCurrentUser())]);
  const listings = await dispatch(fetchAllListings(config, user));

  return { user, listings };
};
